import { useId, useRef, useState } from "react";
import type { UploadedFileDto } from "../../api/client";
import { ApiError, uploadFileWithAuth } from "../../api/client";
import { isRealEventPoster } from "@tih/shared";
import { AdminGalleryPicker } from "./AdminGalleryPicker";

type Tab = "upload" | "gallery";

type Props = {
  value: string;
  onChange: (url: string) => void;
  onUploaded?: (file: UploadedFileDto) => void;
  disabled?: boolean;
  formIdentifier?: string;
  maxUrlLength?: number;
  previewSrc?: string | null;
};

export function AdminPosterField({
  value,
  onChange,
  onUploaded,
  disabled = false,
  formIdentifier = "admin-event-poster",
  maxUrlLength = 500,
  previewSrc,
}: Props) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<Tab>("upload");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  async function handleFile(file: File) {
    if (disabled || uploading) return;
    setUploading(true);
    setUploadError(null);
    try {
      const data = await uploadFileWithAuth(file, { formIdentifier });
      if (!data.mimetype.startsWith("image/")) {
        setUploadError("Please choose an image file.");
        return;
      }
      onChange(data.uploadsUrl);
      onUploaded?.(data);
    } catch (err) {
      setUploadError(err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) void handleFile(file);
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) void handleFile(file);
  }

  const displayPreview =
    previewSrc ?? (isRealEventPoster(value) ? value : null);

  return (
    <div className="admin-poster-field">
      <span className="admin-poster-field-label">
        Poster image <em className="admin-field-optional">(optional — add anytime)</em>
      </span>

      <div className="admin-poster-tabs">
        <button
          type="button"
          className={`admin-poster-tab${tab === "upload" ? " admin-poster-tab--active" : ""}`}
          onClick={() => setTab("upload")}
          disabled={disabled}
        >
          Upload
        </button>
        <button
          type="button"
          className={`admin-poster-tab${tab === "gallery" ? " admin-poster-tab--active" : ""}`}
          onClick={() => {
            setTab("gallery");
            setGalleryOpen(true);
          }}
          disabled={disabled}
        >
          Gallery
        </button>
      </div>

      {tab === "upload" && (
        <div
          className={`admin-dropzone${dragOver ? " admin-dropzone--active" : ""}${uploading ? " admin-dropzone--busy" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !disabled && !uploading && inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
        >
          <input
            ref={inputRef}
            id={`${id}-file`}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
            onChange={onPickFile}
            disabled={disabled || uploading}
            hidden
          />
          {uploading ? (
            <span>Uploading…</span>
          ) : (
            <>
              <strong>Drop poster here</strong>
              <span>or click to browse</span>
            </>
          )}
        </div>
      )}

      {uploadError && <div className="form-error" style={{ marginTop: 8 }}>{uploadError}</div>}

      {displayPreview && (
        <div className="admin-poster-preview">
          <img src={displayPreview} alt="Poster preview" />
          {value && (
            <button
              type="button"
              className="btn btn-ghost admin-poster-replace"
              disabled={disabled || uploading}
              onClick={() => inputRef.current?.click()}
            >
              Replace
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        className="admin-poster-advanced-toggle"
        onClick={() => setAdvancedOpen((v) => !v)}
      >
        {advancedOpen ? "Hide" : "Show"} advanced URL
      </button>
      {advancedOpen && (
        <label htmlFor={`${id}-url`} className="admin-field" style={{ marginTop: 8 }}>
          <span>URL or path</span>
          <input
            id={`${id}-url`}
            type="text"
            maxLength={maxUrlLength}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder="/uploads/… or https://…"
          />
        </label>
      )}

      <AdminGalleryPicker
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onSelect={(file) => {
          onChange(file.uploadsUrl);
          onUploaded?.(file);
        }}
        formIdentifier={formIdentifier}
      />
    </div>
  );
}
